import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useCallback } from "react";

import {
  Cell,
  FONT_WEIGHT_MEDIUM,
  Icon,
  StyledTableHead,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  transientOptions,
  useSearch,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";
import type { InteractModuleListItem } from "@skilitics/nautilus-endpoint-definition";

import { useLandingPageSettings } from "../../../LandingPageSettingsContext";
import { getModulePathDescriptor } from "../../../shared";
import type { Ordering, OrderingDirection } from "../../../types";
import { columns, formatStrings, type Column } from "./columns";
import { Folder } from "./Folder";
import { ListItem } from "./ListItem";
import { MenuContainer } from "./ListItemElements";

const Container = styled.div`
  position: relative;

  width: calc(100% - (2 * var(--page-padding-inline)));

  padding: 0;
  margin: 0 var(--page-padding-inline);

  border-radius: 5px;

  overflow: visible;

  --sticky-z-index: 2;
  --table-head-height: 32px;
  --scale: 0.985;
`;

const Separator = styled.div`
  position: sticky;
  top: calc(var(--table-head-height) + 12px);

  width: 100%;
  height: 1px;

  background-color: ${({ theme }) => theme.outline};

  z-index: calc(var(--sticky-z-index) + 1);

  transform: scale(0.99);
`;

const sharedCss = css`
  cursor: pointer;
  transform: scale(1);

  transition: transform 125ms ease-out;

  ${MenuContainer} {
    opacity: 1;

    pointer-events: auto;
  }
`;

const Mask = styled.div`
  position: sticky;
  top: -2px;

  width: 100%;
  height: 4px;
  /* Sits in flow so it tracks the scroll area, but takes no vertical space. */
  margin-bottom: -4px;

  background-color: var(--background-color);
  z-index: var(--sticky-z-index);
`;

const ListViewTableContainer = styled(TableContainer)`
  position: relative;
  overflow: visible;
  display: flex;
  background: none;

  > table {
    border-collapse: separate;
    min-width: var(--min-width);

    thead {
      background: var(--background-color);

      position: sticky;
      top: 0;
      height: var(--table-head-height);
      z-index: var(--sticky-z-index);
    }

    th {
      padding: 6px 8px;
      border: none;
      background: none;

      font-size: calc(1 / var(--scale) * 12px);
      transform: scale(var(--scale));

      line-height: var(--table-head-height);
    }

    tbody {
      background: none;
    }

    tr {
      background: none;
      transition: transform 250ms ease-out;
      user-select: none;
    }

    td {
      padding: 0;
      border-block-start: solid 5px var(--background-color);
      border-block-end: solid 5px var(--background-color);

      &:not(:first-of-type) {
        background-color: ${({ theme }) => theme.surface};
      }
    }

    tbody tr {
      transform: scale(var(--scale));
    }

    tbody tr:hover {
      ${sharedCss}
    }

    tbody tr[data-hovered="true"] {
      ${sharedCss}
    }

    td:not(:first-of-type) {
      padding-inline-start: 10px;
    }

    tr td:last-of-type {
      border-start-end-radius: 15px;
      border-end-end-radius: 15px;
    }
  }
`;

const StyledColumnCell = styled(Cell)`
  font-size: 16px;
  font-weight: ${FONT_WEIGHT_MEDIUM};

  user-select: none;
`;

const ColumnLabel = styled.span``;

const Indicator = styled(Icon, transientOptions)<{ $orderingDirection: OrderingDirection; $show: boolean }>`
  transform: ${({ $orderingDirection }) => ($orderingDirection === "ascending" ? "rotateX(180deg)" : "rotateX(0deg)")};

  opacity: ${({ $show }) => ($show ? 1 : 0)};

  transition:
    opacity 300ms ease-in-out,
    transform 300ms ease-in-out;
`;

const ColumnContainer = styled("div", transientOptions)<{
  $orderingDirection: OrderingDirection;
  $show: boolean;
  $selectable: boolean;
}>`
  display: flex;
  gap: 8px;

  cursor: ${({ $selectable }) => ($selectable ? "pointer" : "default")};

  &:hover ${Indicator} {
    opacity: ${({ $show, $selectable }) => ($selectable ? ($show ? 1 : 0.3) : 0)};
    transform: ${({ $orderingDirection, $show }) =>
      $show ? ($orderingDirection === "ascending" ? "rotateX(180deg)" : "rotateX(0deg)") : "rotateX(180deg)"};

    transition: ${({ $show }) =>
      $show
        ? "opacity 300ms ease-in-out, transform 300ms ease-in-out"
        : "opacity 300ms ease-in-out, transform 0ms ease-in-out"};
  }
`;

type Props = {
  modules: InteractModuleListItem[];
};

const clickableColumnIds: Array<Column["id"]> = ["titleAndDescription", "tags", "modified"];

const orderingByColumnId: Partial<Record<Column["id"], Ordering>> = {
  titleAndDescription: "title",
  tags: "tags",
  modified: "updatedOn",
};

const columnIdByOrdering = Object.fromEntries(
  Object.entries(orderingByColumnId).map(([key, value]) => [value, key]),
) as Partial<Record<Ordering, Column["id"]>>;

export const ListView = ({ modules }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const { filteredData, results } = useSearch<InteractModuleListItem>();

  const { getFilterSet, ordering, orderingDirection, setOrdering, setOrderingDirection } = useLandingPageSettings();

  const createHandleColumnClick = useCallback(
    (id: Column["id"]) => () => {
      if (!clickableColumnIds.includes(id)) return;

      if (columnIdByOrdering[ordering] === id) {
        setOrderingDirection(orderingDirection === "ascending" ? "descending" : "ascending");
      } else {
        setOrderingDirection("ascending");
        setOrdering(orderingByColumnId[id] as Ordering);
      }
    },
    [ordering, orderingDirection, setOrdering, setOrderingDirection],
  );

  const addedFolders: string[] = [];

  return (
    <Container>
      <Mask />
      <Separator />
      <ListViewTableContainer>
        <Table stickyHeader size="small" data-test="list-container">
          <StyledTableHead data-test="list-header">
            <TableRow>
              {columns.map(({ id, width, align }) => (
                <StyledColumnCell
                  key={id}
                  align={align}
                  css={id === "thumbnail" ? { minWidth: width, maxWidth: width, width } : { width }}
                  onClick={createHandleColumnClick(id)}
                  data-test="list-heading"
                >
                  <ColumnContainer
                    $orderingDirection={orderingDirection}
                    $show={columnIdByOrdering[ordering] === id}
                    $selectable={clickableColumnIds.includes(id)}
                  >
                    <ColumnLabel>{formatMessage(id)}</ColumnLabel>
                    <Indicator
                      icon="angle-down"
                      size={14}
                      $orderingDirection={orderingDirection}
                      $show={columnIdByOrdering[ordering] === id}
                    />
                  </ColumnContainer>
                </StyledColumnCell>
              ))}
            </TableRow>
          </StyledTableHead>

          <TableBody data-test="list-content">
            {/* TODO: awaiting final design for location of AddButton */}
            {/* <TableRow>
              <Cell colSpan={columns.length}>
                <AddButton />
              </Cell>
            </TableRow> */}
            {modules.map((module) => {
              const filterPath = getFilterSet("folder")?.metadata as string;

              const { filter, isLeaf, title } = getModulePathDescriptor(module, filterPath);

              if (filter && !isLeaf) {
                if (!addedFolders.includes(filter)) {
                  addedFolders.push(filter);

                  const moduleCount = filteredData?.filter((mod) => mod.title.startsWith(filter)).length;
                  const searchCount = results?.filter((mod) => mod.title.startsWith(filter)).length;

                  return (
                    <Folder
                      key={module.title}
                      title={title}
                      filter={filter}
                      moduleCount={moduleCount}
                      searchCount={searchCount}
                    />
                  );
                }

                return null;
              }

              return <ListItem key={module.id} module={module} title={title} />;
            })}
          </TableBody>
        </Table>
      </ListViewTableContainer>
    </Container>
  );
};
